import { CancellationFee } from "app/shared/model/CancellationFee";
import {
  CancellationFeeOfferingDto,
  UpsertOfferingsRequest,
} from "app/shared/model/EditPricelist";
import { CreateOfferings, PutPricelistForSchools } from "./getEditProductOfferings";

function normalizeSchoolId(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const match = /^(?:School-)?(\d+)$/i.exec(trimmed);
  const numericValue = match ? Number(match[1]) : Number(trimmed);
  return Number.isInteger(numericValue) && numericValue > 0 ? numericValue : null;
}

function toSchoolIds(
  schoolIds: (string | number | undefined | null)[] | undefined
): { id: number }[] {
  if (!Array.isArray(schoolIds)) return [];
  return schoolIds
    .map(normalizeSchoolId)
    .filter((id): id is number => id !== null)
    .map((id) => ({ id }));
}

function buildOffering(
  fee: Partial<CancellationFee>,
  cancellationFeeId: number,
  pricingYear: number,
  isNewOffering: boolean
): CancellationFeeOfferingDto {
  return {
    offeringType: "cancellationFee",
    type: "CancellationFee",
    productIdText: `CancellationFee-${cancellationFeeId}`,
    name: String(fee.name ?? "").trim(),
    hasPendingChanges: true,
    isUpdated: !isNewOffering,
    isNew: isNewOffering,
    cancellationFeeId: { id: cancellationFeeId },
    activeFromPricingYear: pricingYear,
    schoolIds: toSchoolIds(fee.schoolIds),
  };
}

export interface SyncCancellationFeeOfferingResult {
  ok: boolean;
  message?: string;
  errors?: string[];
}

function isOfferingNotFoundError(message?: string, errors?: string[]): boolean {
  const text = [message ?? "", ...(errors ?? [])].join(" ").toLowerCase();
  return text.includes("offering not found");
}

async function syncOfferings(
  request: UpsertOfferingsRequest,
  pricingYear: number,
  divisionId: number,
  useCreate: boolean
): Promise<SyncCancellationFeeOfferingResult> {
  const result = useCreate
    ? await CreateOfferings(request, pricingYear, divisionId)
    : await PutPricelistForSchools(request, pricingYear, divisionId);

  return {
    ok: result.ok,
    message: result.message,
    errors: result.errors,
  };
}

export async function syncCancellationFeeOffering(
  fee: Partial<CancellationFee>,
  cancellationFeeId: number,
  pricingYear: number,
  _pricingYearId: number,
  divisionId: number,
  isNewCancellationFee: boolean
): Promise<SyncCancellationFeeOfferingResult> {
  if (isNewCancellationFee) {
    const createRequest: UpsertOfferingsRequest = {
      type: "CancellationFee",
      offerings: [buildOffering(fee, cancellationFeeId, pricingYear, true)],
    };
    return syncOfferings(createRequest, pricingYear, divisionId, true);
  }

  const updateRequest: UpsertOfferingsRequest = {
    type: "CancellationFee",
    offerings: [buildOffering(fee, cancellationFeeId, pricingYear, false)],
  };
  const updateResult = await syncOfferings(updateRequest, pricingYear, divisionId, false);

  if (updateResult.ok) {
    return updateResult;
  }

  if (isOfferingNotFoundError(updateResult.message, updateResult.errors)) {
    const createRequest: UpsertOfferingsRequest = {
      type: "CancellationFee",
      offerings: [buildOffering(fee, cancellationFeeId, pricingYear, true)],
    };
    return syncOfferings(createRequest, pricingYear, divisionId, true);
  }

  return updateResult;
}